import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, FormGroup, Label, Input, Alert, FormFeedback } from "reactstrap";

const PASSWORD_MIN_LENGTH = 7;

const validatePassword = (password) => {
  if (!password || password.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`;
  }
  return null;
};

const ResetPasswordModal = ({ open, onClose, organization, user, onSubmit }) => {
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setNewPassword("");
      setError("");
    }
  }, [open]);

  const handleSubmit = async () => {
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    try {
      setIsSubmitting(true);
      await onSubmit((user || organization)?.id, newPassword);
      setNewPassword("");
      onClose();
    } catch (e) {
      console.error("Error resetting password:", e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setNewPassword("");
    setError("");
    onClose();
  };

  const isInvalid = error !== "";

  return (
    <Modal isOpen={open} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>
        <span style={{ fontSize: "18px", fontWeight: "700" }}>
          {user ? 'Reset User Password' : 'Reset Organization Password'}
        </span>
      </ModalHeader>
      <ModalBody>
        {user && (
          <Alert color="info" className="mb-3">
            <div><strong>Name:</strong> {user.name || 'N/A'}</div>
            <div><strong>Email:</strong> {user.email || 'N/A'}</div>
            <div><strong>Organization:</strong> {user.organizationName || 'N/A'}</div>
          </Alert>
        )}
        {organization && !user && (
          <Alert color="info" className="mb-3">
            <div><strong>Organization:</strong> {organization.name || "N/A"}</div>
            <div><strong>Website:</strong> {organization.website || "N/A"}</div>
            <div><strong>Type:</strong> {organization.type || "N/A"}</div>
          </Alert>
        )}

        <FormGroup>
          <Label style={{ fontWeight: "500", fontSize: "14px", marginBottom: "10px" }}>
            New Password *
          </Label>
          <Input
            type="password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              if (error) setError(validatePassword(e.target.value) || "");
            }}
            invalid={isInvalid}
            placeholder="Min 7 characters"
            minLength={PASSWORD_MIN_LENGTH}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <FormFeedback>{error}</FormFeedback>
          <small className="text-muted">
            Password must be at least 7 characters long.
          </small>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <Spinner size="sm" color="light" />
          ) : (
            "Reset Password"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ResetPasswordModal;
